import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Space,
  Modal,
  Form,
  message,
  DatePicker,
  Radio,
  InputNumber,
  Tag,
  Tooltip,
  Avatar,
  Statistic,
  Divider,
  Typography
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ExportOutlined,
  UserOutlined,
  HomeOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  RiseOutlined,
  FireOutlined,
  StarOutlined,
  FieldTimeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';

import api from '../services/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const LeadsPage = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    totalLeads: 0,
    hotLeads: 0,
    warmLeads: 0,
    coldLeads: 0,
    finalizedLeads: 0,
    notInterested: 0
  });
  const [form] = Form.useForm();
  const [editingLead, setEditingLead] = useState(null);
  const [viewingLead, setViewingLead] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const navigate = useNavigate();


  // Fetch leads on component mount and when filters change
  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [searchText, statusFilter, sourceFilter, dateRange]);

  // Fetch leads from API
  const fetchLeads = async () => {
    setTableLoading(true);
    try {
      const params = {
        search: searchText || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD')
      };

      const response = await api.get('/leads', { params });

      if (response.success) {
        setLeads(response.data);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await api.get('/leads/stats');
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Handle Add Lead button click
  const showAddLeadModal = () => {
    setEditingLead(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // Handle Edit Lead
  const handleEditLead = (record) => {
    setEditingLead(record);
    form.setFieldsValue({
      dateOfInquiry: record.dateOfInquiry ? dayjs(record.dateOfInquiry) : null,
      source: record.leadSource,
      campaign: record.campaignName,
      fullName: record.fullName,
      email: record.email,
      contact: record.contactNumber,
      alternateContact: record.alternateNumber,
      address: record.address,
      city: record.city,
      state: record.state,
      pincode: record.pincode,
      requirementType: record.requirementType,
      capacity: record.capacity,
      budget: record.budget,
      timeline: record.preferredTimeline,
      roofType: record.roofType,
      roofArea: record.roofArea,
      shadingIssues: record.shadingIssues,
      powerConnection: record.powerConnection,
      monthlyBill: record.monthlyBill,
      governmentScheme: record.governmentScheme,
      status: record.leadStatus,
      priority: record.priority,
      nextFollowUp: record.nextFollowUp ? dayjs(record.nextFollowUp) : null,
      assignedTo: record.assignedTo,
      notes: record.notes
    });
    setIsModalVisible(true);
  };

  // Handle view lead
  const handleViewLead = (record) => {
    setViewingLead(record);
    setIsViewModalVisible(true);
  };

  // Handle modal cancel
  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingLead(null);
  };

  // Handle form submit - FIXED: Remove undefined values
  // Handle form submit - FIXED: Use field names that backend expects
// Handle form submit - FIXED: Handle email properly
const handleSubmit = async () => {
  try {
    setLoading(true);
    const values = await form.validateFields();

    // Format the data for API
    const leadData = {
      dateOfInquiry: values.dateOfInquiry?.format('YYYY-MM-DD'),
      source: values.source,
      campaign: values.campaign || null,

      fullName: values.fullName,
      // Only include email if it has a value, otherwise don't send it at all
      ...(values.email && values.email.trim() !== '' ? { email: values.email.trim() } : {}),
      
      contact: values.contact,
      ...(values.alternateContact && values.alternateContact.trim() !== '' ? { alternateContact: values.alternateContact.trim() } : {}),
      
      address: values.address,
      city: values.city,
      state: values.state,
      pincode: values.pincode,

      requirementType: values.requirementType,
      ...(values.capacity != null ? { capacity: parseFloat(values.capacity) } : {}),
      ...(values.budget != null ? { budget: parseFloat(values.budget) } : {}),
      timeline: values.timeline,
      ...(values.roofType && values.roofType.trim() !== '' ? { roofType: values.roofType } : {}),
      ...(values.roofArea ? { roofArea: parseFloat(values.roofArea) } : {}),
      ...(values.shadingIssues && values.shadingIssues.trim() !== '' ? { shadingIssues: values.shadingIssues } : {}),
      ...(values.powerConnection && values.powerConnection.trim() !== '' ? { powerConnection: values.powerConnection } : {}),
      ...(values.monthlyBill ? { monthlyBill: parseFloat(values.monthlyBill) } : {}),
      ...(values.governmentScheme && values.governmentScheme.trim() !== '' ? { governmentScheme: values.governmentScheme } : {}),

      status: values.status,
      ...(values.priority && values.priority.trim() !== '' ? { priority: values.priority } : {}),
      ...(values.nextFollowUp ? { nextFollowUp: values.nextFollowUp?.format('YYYY-MM-DD') } : {}),
      ...(values.assignedTo && values.assignedTo.trim() !== '' ? { assignedTo: values.assignedTo } : {}),
      ...(values.notes && values.notes.trim() !== '' ? { notes: values.notes } : {})
    };

    console.log('Submitting lead:', leadData);

    const token = localStorage.getItem('token');

    let response;

    if (editingLead) {
      response = await api.put(`/leads/${editingLead.id}`, leadData);
      message.success('Lead updated successfully!');
    } else {
      response = await api.post('/leads', leadData);
      message.success('Lead added successfully!');
    }

    console.log('Response:', response.data);

    setIsModalVisible(false);
    form.resetFields();
    setEditingLead(null);

    fetchLeads();
    fetchStats();

    // If lead is finalized, redirect to Customer form with prefill
    if (values.status === 'OrderFinalized') {
      const lead = response?.data?.data || response?.data;
      navigate('/customers', {
        state: {
          prefillFromLead: lead
        }
      });
    }

  } catch (error) {
    console.error('Submission failed:', error);

    if (error.response) {
      console.error('Error response:', error.response.data);
      console.error('Error status:', error.response.status);

      if (error.response.status === 401) {
        message.error('Authentication failed. Please login again.');
      } else if (error.response.status === 400) {
        const errorMsg = error.response.data?.message || 
                        error.response.data?.errors?.[0]?.msg || 
                        error.response.data?.errors?.[0] || 
                        'Please check your input';
        message.error(errorMsg);
      } else {
        message.error(error.response.data?.message || 'Server error');
      }
    } else if (error.request) {
      console.error('No response:', error.request);
      message.error('Cannot connect to server. Please check if backend is running.');
    } else {
      console.error('Error:', error.message);
      message.error('Error: ' + error.message);
    }
  } finally {
    setLoading(false);
  }
};
  // Handle delete lead
  const handleDeleteLead = (record) => {
    Modal.confirm({
      title: 'Delete Lead',
      content: `Are you sure you want to delete lead for ${record.fullName}?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await api.delete(`/leads/${record.id}`);
          message.success('Lead deleted successfully');
          fetchLeads();
          fetchStats();
        } catch (error) {
          message.error('Failed to delete lead');
        }
      }
    });
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    Modal.confirm({
      title: 'Bulk Delete',
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected leads?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          // Delete one by one
          for (const id of selectedRowKeys) {
            await api.delete(`/leads/${id}`);
          }
          message.success(`${selectedRowKeys.length} leads deleted successfully`);
          setSelectedRowKeys([]);
          fetchLeads();
          fetchStats();
        } catch (error) {
          message.error('Failed to delete some leads');
        }
      }
    });
  };

  // Handle export
  const handleExport = async () => {
    try {
      const response = await api.get('/leads/export', {
        responseType: 'blob'
      });

      // Since response.data is already returned by interceptor, but wait
      // The interceptor returns response.data. If responseType is blob,
      // response.data IS the blob.
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success('Leads exported successfully');
    } catch (error) {
      message.error('Failed to export leads');
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'Hot': 'red',
      'Warm': 'orange',
      'Cold': 'blue',
      'OrderFinalized': 'green',
      'NotInterested': 'gray'
    };
    return colors[status] || 'default';
  };

  // Table columns - FIXED: Using div instead of Space to avoid warning
  const columns = [
    {
      title: 'Customer',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      render: (text, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div>{text}</div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>{record.requirementType}</div>
          </div>
        </Space>
      )
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
          <div><PhoneOutlined /> {record.contactNumber}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}><MailOutlined /> {record.email || 'N/A'}</div>
        </div>
      )
    },
    {
      title: 'Location',
      dataIndex: 'city',
      key: 'city',
      width: 150,
      render: (text, record) => (
        <div><EnvironmentOutlined /> {text}, {record.state}</div>
      )
    },
    {
      title: 'Source',
      dataIndex: 'leadSource',
      key: 'leadSource',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'leadStatus',
      key: 'leadStatus',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      )
    },
    {
      title: 'Capacity',
      dataIndex: 'capacity',
      key: 'capacity',
      width: 100,
      render: (capacity) => `${capacity} kW`
    },
    {
      title: 'Budget',
      dataIndex: 'budget',
      key: 'budget',
      width: 120,
      render: (budget) => `₹${budget?.toLocaleString()}`
    },
    {
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleViewLead(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEditLead(record)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteLead(record)} />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header with Add Lead Button */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '600', margin: 0 }}>Leads Management</h1>
          <p style={{ color: '#6b7280', marginTop: '4px' }}>Manage and track your sales pipeline</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showAddLeadModal}
          size="large"
        >
          Add New Lead
        </Button>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={4}>
          <Card>
            <Statistic
              title="Total Leads"
              value={stats.totalLeads}
              valueStyle={{ color: '#1890ff' }}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Hot Leads"
              value={stats.hotLeads}
              valueStyle={{ color: '#f5222d' }}
              prefix={<FireOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Warm Leads"
              value={stats.warmLeads}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<StarOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Cold Leads"
              value={stats.coldLeads}
              valueStyle={{ color: '#52c41a' }}
              prefix={<FieldTimeOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Finalized"
              value={stats.finalizedLeads}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Not Interested"
              value={stats.notInterested}
              valueStyle={{ color: '#8c8c8c' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters Section */}
      <Card style={{ marginBottom: '24px' }}>
        <Space size="middle" wrap>
          <Input
            placeholder="Search leads..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />

          <Select
            placeholder="Filter by Status"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="all">All Status</Option>
            <Option value="Hot">Hot</Option>
            <Option value="Warm">Warm</Option>
            <Option value="Cold">Cold</Option>
            <Option value="OrderFinalized">Order Finalized</Option>
            <Option value="NotInterested">Not Interested</Option>
          </Select>

          <Select
            placeholder="Filter by Source"
            value={sourceFilter}
            onChange={setSourceFilter}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="all">All Sources</Option>
            <Option value="Website">Website</Option>
            <Option value="Referral">Referral</Option>
            <Option value="GovernmentScheme">Government Scheme</Option>
            <Option value="ColdCall">Cold Call</Option>
            <Option value="SocialMedia">Social Media</Option>
          </Select>

          <RangePicker
            style={{ width: 250 }}
            onChange={(dates) => setDateRange(dates)}
          />

          <Button icon={<ExportOutlined />} onClick={handleExport}>
            Export
          </Button>
        </Space>

        {/* Bulk Actions */}
        {selectedRowKeys.length > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
            <Space>
              <span>{selectedRowKeys.length} leads selected</span>
              <Button danger icon={<DeleteOutlined />} onClick={handleBulkDelete}>
                Delete Selected
              </Button>
            </Space>
          </div>
        )}
      </Card>

      {/* Leads Table */}
      <Card>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={leads}
          rowKey="id"
          scroll={{ x: 1500 }}
          loading={tableLoading}
          pagination={{
            total: leads.length,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} leads`,
          }}
        />
      </Card>

      {/* Add/Edit Lead Modal - styled similar to ChannelPartnerForm */}
      <Modal
        open={isModalVisible}
        onCancel={handleCancel}
        width={800}
        maskClosable={false}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 'Cold',
            priority: 'Medium',
            requirementType: 'Residential',
            timeline: 'OneToThreeMonths',
            source: 'Website',
            shadingIssues: 'No',
            governmentScheme: 'Yes'
          }}
        >
          {/* Header block similar to ChannelPartnerForm */}
          <div style={{ marginBottom: 24 }}>
            <Space size="middle">
              <Avatar size="large" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {editingLead ? 'Edit Lead Details' : 'Add New Lead'}
                </Title>
                <Text type="secondary">Capture customer inquiry and requirement details.</Text>
              </div>
            </Space>
          </div>

          <Divider orientation="left">
            <Space>Lead Information</Space>
          </Divider>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="dateOfInquiry"
                label="Date of Inquiry"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker style={{ width: '100%' }} placeholder="Select date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="source"
                label="Lead Source"
                rules={[{ required: true, message: 'Please select source' }]}
              >
                <Select placeholder="Select source">
                  <Option value="Website">Website</Option>
                  <Option value="Referral">Referral</Option>
                  <Option value="GovernmentScheme">Government Scheme</Option>
                  <Option value="ColdCall">Cold Call</Option>
                  <Option value="SocialMedia">Social Media</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="campaign"
                label="Campaign Name / Ad Reference"
              >
                <Input placeholder="Enter campaign name or ad reference" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>Customer Details</Space>
          </Divider>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="fullName"
                label="Full Name"
                rules={[
                  { required: true, message: 'Please enter full name' },
                  { pattern: /^[a-zA-Z\s.'-]+$/, message: 'Name cannot contain numbers or special characters' }
                ]}
              >
                <Input placeholder="Enter customer name" prefix={<UserOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  {
                    type: 'email',
                    message: 'Please enter a valid email'
                  }
                ]}
              >
                <Input placeholder="Enter email address (optional)" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contact"
                label="Contact Number"
                rules={[
                  { required: true, message: 'Please enter contact number' },
                  {
                    pattern: /^[6-9]\d{9}$/,
                    message: 'Please enter a valid 10-digit Indian mobile number starting with 6-9'
                  }
                ]}
              >
                <Input placeholder="Enter 10-digit mobile number" prefix={<PhoneOutlined />} maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="alternateContact"
                label="Alternate Number (Optional)"
                rules={[
                  {
                    pattern: /^[6-9]\d{9}$/,
                    message: 'Please enter a valid 10-digit Indian mobile number starting with 6-9'
                  }
                ]}
              >
                <Input placeholder="Enter 10-digit mobile number" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="address"
                label="Address"
                rules={[{ required: true, message: 'Please enter address' }]}
              >
                <TextArea rows={2} placeholder="Enter full address" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="state"
                label="State"
                rules={[
                  { required: true, message: 'Please enter state' },
                  {
                    pattern: /^[A-Za-z\s]+$/,
                    message: 'State should contain only letters and spaces'
                  },
                  {
                    validator: (_, value) => {
                      if (!value || value.trim().length >= 2) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('State name must be at least 2 characters'));
                    }
                  }
                ]}
              >
                <Input placeholder="Enter state name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="pincode"
                label="Pin Code"
                rules={[
                  { required: true, message: 'Please enter pin code' },
                  {
                    pattern: /^[1-9][0-9]{5}$/,
                    message: 'Please enter a valid 6-digit Indian PIN code'
                  }
                ]}
              >
                <Input placeholder="Enter 6-digit PIN code" maxLength={6} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>Requirement Details</Space>
          </Divider>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="requirementType"
                label="Type of Requirement"
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Radio.Group>
                  <Radio value="Residential"><HomeOutlined /> Residential</Radio>
                  <Radio value="Commercial"><ShopOutlined /> Commercial</Radio>
                  <Radio value="Industrial"><ThunderboltOutlined /> Industrial</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="capacity"
                label="Required Capacity (kW)"
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} placeholder="Enter capacity" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="budget"
                label="Approximate Budget (₹)"
                rules={[{ required: true, message: 'Please enter budget' }]}
              >
                <InputNumber
                  min={0}
                  step={10000}
                  style={{ width: '100%' }}
                  placeholder="Enter budget"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="timeline"
                label="Preferred Installation Timeline"
                
              >
                <Select placeholder="Select timeline">
                  <Option value="Immediately">Immediately</Option>
                  <Option value="OneToThreeMonths">1-3 Months</Option>
                  <Option value="ThreeToSixMonths">3-6 Months</Option>
                  <Option value="MoreThanSixMonths">More than 6 Months</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="roofType"
                label="Roof Type"
              >
                <Select placeholder="Select roof type">
                  <Option value="Rcc">Rcc</Option>
                  <Option value="Tin">Tin</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="roofArea"
                label="Available Roof Area (sq. ft.)"
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Enter area" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="shadingIssues"
                label="Shading Issues?"
              >
                <Radio.Group>
                  <Radio value="Yes">Yes</Radio>
                  <Radio value="No">No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="powerConnection"
                label="Power Connection"
              >
                <Select placeholder="Select connection type">
                  <Option value="SinglePhase">Single Phase</Option>
                  <Option value="ThreePhase">Three Phase</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="monthlyBill"
                label="Average Monthly Electricity Bill (₹)"
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="Enter amount"
                  formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/₹\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="governmentScheme"
                label="Government Scheme Applicable?"
              >
                <Radio.Group>
                  <Radio value="Yes">Yes</Radio>
                  <Radio value="No">No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Space>Follow-up & Tracking</Space>
          </Divider>
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="status"
                label="Lead Status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="Hot">Hot</Option>
                  <Option value="Warm">Warm</Option>
                  <Option value="Cold">Cold</Option>
                  <Option value="NotInterested">Not Interested</Option>
                  <Option value="OrderFinalized">Order Finalized</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="Priority Level"
              >
                <Select placeholder="Select priority">
                  <Option value="High">High</Option>
                  <Option value="Medium">Medium</Option>
                  <Option value="Low">Low</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="nextFollowUp"
                label="Next Follow-up Date"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="Select date"
                  disabledDate={(current) => {
                    const dateOfInquiry = form.getFieldValue('dateOfInquiry');
                    return dateOfInquiry ? current && current < dateOfInquiry.startOf('day') : false;
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="assignedTo"
                label="Assigned To"
              >
                <Input placeholder="Enter salesperson name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="notes"
                label="Notes / Remarks"
              >
                <TextArea rows={3} placeholder="Add any additional notes or remarks here..." />
              </Form.Item>
            </Col>
          </Row>

          {/* Sticky footer like ChannelPartnerForm */}
          <div
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: '100%',
              borderTop: '1px solid #e9e9e9',
              padding: '10px 16px',
              background: '#fff',
              textAlign: 'left',
              zIndex: 1,
            }}
          >
            <Space>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="primary" loading={loading} onClick={handleSubmit} size="large">
                {editingLead ? 'Update Lead' : 'Add Lead'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* View Lead Modal */}
      <Modal
        open={isViewModalVisible}
        onCancel={() => { setIsViewModalVisible(false); setViewingLead(null); }}
        footer={<Button onClick={() => { setIsViewModalVisible(false); setViewingLead(null); }}>Close</Button>}
        title={<Space><EyeOutlined /><span>Lead Details</span></Space>}
        width={700}
      >
        {viewingLead && (
          <div style={{ padding: '8px 0' }}>
            <Row gutter={[16, 12]}>
              <Col span={12}><Typography.Text type="secondary">Full Name</Typography.Text><div><Typography.Text strong>{viewingLead.fullName}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Status</Typography.Text><div><Tag color={viewingLead.status === 'Hot' ? 'red' : viewingLead.status === 'Warm' ? 'orange' : viewingLead.status === 'Cold' ? 'blue' : viewingLead.status === 'OrderFinalized' ? 'green' : 'gray'}>{viewingLead.status}</Tag></div></Col>
              <Col span={12}><Typography.Text type="secondary">Contact</Typography.Text><div><Typography.Text>{viewingLead.contactNumber || viewingLead.contact || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Email</Typography.Text><div><Typography.Text>{viewingLead.email || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">City</Typography.Text><div><Typography.Text>{viewingLead.city || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">State</Typography.Text><div><Typography.Text>{viewingLead.state || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Source</Typography.Text><div><Typography.Text>{viewingLead.source || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Priority</Typography.Text><div><Typography.Text>{viewingLead.priority || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Requirement Type</Typography.Text><div><Typography.Text>{viewingLead.requirementType || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Capacity (kW)</Typography.Text><div><Typography.Text>{viewingLead.capacity || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Budget</Typography.Text><div><Typography.Text>{viewingLead.budget ? `₹${viewingLead.budget.toLocaleString()}` : '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Timeline</Typography.Text><div><Typography.Text>{viewingLead.timeline || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Date of Inquiry</Typography.Text><div><Typography.Text>{viewingLead.dateOfInquiry ? dayjs(viewingLead.dateOfInquiry).format('DD/MM/YYYY') : '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Follow Up Date</Typography.Text><div><Typography.Text>{viewingLead.nextFollowUp ? dayjs(viewingLead.nextFollowUp).format('DD/MM/YYYY') : '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Shading Issues</Typography.Text><div><Typography.Text>{viewingLead.shadingIssues || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Govt Scheme</Typography.Text><div><Typography.Text>{viewingLead.governmentScheme || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Assigned To</Typography.Text><div><Typography.Text>{viewingLead.assignedTo || '—'}</Typography.Text></div></Col>
              <Col span={12}><Typography.Text type="secondary">Address</Typography.Text><div><Typography.Text>{viewingLead.address || '—'}</Typography.Text></div></Col>
              {viewingLead.notes && <Col span={24}><Typography.Text type="secondary">Notes</Typography.Text><div><Typography.Text>{viewingLead.notes}</Typography.Text></div></Col>}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LeadsPage;
